import { useEffect, useState } from "react";
import { AlarmClock, MapPin, Clock, ExternalLink, Navigation, Plane } from "lucide-react";
import api from "@/lib/api";
import { raceMapUrl, raceFlagOff } from "@/components/RaceJoin";
import { planSummary } from "@/components/RaceTravelPlanDialog";

const DAY = 86400000;

/** Dashboard reminder cards for races the runner joined that start within 7 days. */
export default function RaceDayReminder() {
  const [races, setRaces] = useState([]);

  useEffect(() => {
    api.get("/races").then((r) => setRaces(r.data || [])).catch(() => {});
  }, []);

  const now = Date.now();
  const soon = races
    .filter((r) => r.joined)
    .map((r) => ({ ...r, ms: new Date(r.date).getTime() - now }))
    .filter((r) => r.ms > -12 * 3600000 && r.ms <= 7 * DAY)
    .sort((a, b) => a.ms - b.ms);

  if (soon.length === 0) return null;

  return (
    <section data-testid="race-day-reminder" className="mt-6 space-y-3">
      {soon.map((r) => <ReminderCard key={r.race_id} race={r} />)}
    </section>
  );
}

function ReminderCard({ race }) {
  const days = Math.ceil(race.ms / DAY);
  const d = new Date(race.date);
  const flagOff = raceFlagOff(race);
  const label = days <= 0 ? "RACE DAY IS HERE" : days === 1 ? "RACE DAY IS TOMORROW" : `RACE WEEK · ${days} DAYS TO GO`;
  const plan = planSummary(race.my_plan);

  return (
    <article
      data-testid={`race-reminder-${race.race_id}`}
      className={`relative hairline overflow-hidden ${days <= 1 ? "border-orange-500 bg-orange-600" : "border-orange-600/60 bg-zinc-950"}`}
    >
      {days > 1 && <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_left,rgba(234,88,12,0.22),transparent_60%)]" aria-hidden="true" />}
      <div className="relative px-6 sm:px-8 py-6 grid grid-cols-1 lg:grid-cols-12 gap-5 items-center">
        <div className="lg:col-span-7 min-w-0">
          <p className={`font-mono text-[10px] tracking-[0.3em] uppercase flex items-center gap-2 ${days <= 1 ? "text-white/80" : "text-orange-500"}`}>
            <AlarmClock size={12} className="animate-pulse" /> {label}
          </p>
          <h3 className="font-display text-3xl sm:text-4xl text-white mt-2 leading-none">{race.title}</h3>
          <div className={`mt-4 grid grid-cols-1 sm:grid-cols-3 gap-px ${days <= 1 ? "bg-white/20" : "bg-zinc-800"} hairline ${days <= 1 ? "border-white/20" : "border-zinc-800"}`}>
            <Meta dark={days <= 1} icon={Clock} label="DATE · FLAG OFF" value={`${d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" }).toUpperCase()}${flagOff ? ` · ${flagOff}` : " · TIME TBA"}`} />
            <Meta dark={days <= 1} icon={MapPin} label="VENUE" value={race.location} />
            <Meta dark={days <= 1} icon={Plane} label="YOUR PLAN" value={plan ? plan.toUpperCase() : "NO PLAN ADDED"} />
          </div>
        </div>
        <div className="lg:col-span-5 flex flex-wrap lg:justify-end gap-2">
          <a
            href={raceMapUrl(race)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`race-reminder-map-${race.race_id}`}
            className={`inline-flex items-center gap-2 font-display tracking-wider text-sm px-5 py-3 transition ${
              days <= 1 ? "bg-white text-orange-700 hover:bg-zinc-100" : "bg-orange-600 hover:bg-orange-500 text-white"
            }`}
          >
            <Navigation size={15} /> OPEN IN MAPS
          </a>
          {race.registration_url && (
            <a
              href={race.registration_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`race-reminder-site-${race.race_id}`}
              className={`inline-flex items-center gap-2 border font-display tracking-wider text-sm px-5 py-3 transition ${
                days <= 1 ? "border-white/50 text-white hover:bg-white/10" : "border-zinc-700 text-zinc-200 hover:border-zinc-500"
              }`}
            >
              RACE SITE <ExternalLink size={13} />
            </a>
          )}
        </div>
      </div>
    </article>
  );
}

function Meta({ icon: Icon, label, value, dark }) {
  return (
    <div className={`${dark ? "bg-orange-600" : "bg-zinc-950"} px-4 py-3 min-w-0`}>
      <p className={`font-mono text-[9px] tracking-[0.3em] uppercase flex items-center gap-1.5 ${dark ? "text-white/70" : "text-zinc-500"}`}>
        <Icon size={10} className={dark ? "text-white" : "text-orange-500"} /> {label}
      </p>
      <p className="font-display text-sm sm:text-base text-white tracking-wide mt-1 leading-tight break-words">{value}</p>
    </div>
  );
}
